import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { email } from '@config';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../context/language';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    justify-content: flex-start;
    padding-top: calc(var(--nav-height) + 24px);
    padding-bottom: 64px;
  }

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  .intro {
    margin: 0 0 16px 4px;
    color: var(--icon-accent);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 14px 2px;
    }
  }

  h3 {
    margin-top: 8px;
    color: var(--slate);
  }

  p {
    margin: 24px 0 0;
    max-width: 640px;
  }

  .quick-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 28px;
    width: 100%;
    max-width: 640px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .quick-stat {
    ${({ theme }) => theme.mixins.boxShadow};
    border-radius: var(--border-radius);
    background: var(--surface);
    border: 1px solid var(--surface-border);
    padding: 14px 16px;

    .label {
      display: block;
      color: var(--light-slate);
      font-size: var(--fz-xxs);
      font-family: var(--font-mono);
      margin-bottom: 6px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .value {
      color: var(--lightest-slate);
      font-weight: 600;
      font-size: var(--fz-lg);
    }
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    color: var(--icon-accent);
    border-color: var(--icon-accent);
    margin-top: 44px;

    &:hover,
    &:focus-visible {
      box-shadow: 4px 4px 0 0 var(--icon-accent);
    }
  }
`;

const Hero = () => {
  const { content } = useLanguage();
  const hero = content.hero;
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, [prefersReducedMotion]);

  const one = <p className="intro">{hero.intro}</p>;
  const two = <h2 className="big-heading">{hero.title}</h2>;
  const three = <h3 className="big-heading">{hero.subtitle}</h3>;
  const four = <p>{hero.description}</p>;

  const five = (
    <div className="quick-stats">
      {hero.quickStats.map((item, i) => (
        <div className="quick-stat" key={i}>
          <span className="label">{item.label}</span>
          <span className="value">{item.value}</span>
        </div>
      ))}
    </div>
  );

  const six = (
    <a className="email-link" href={`mailto:${email}`}>
      {hero.cta}
    </a>
  );

  const items = [one, two, three, four, five, six];

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
